<script setup lang="ts">
const route = useRoute();
const localePath = useLocalePath();
const { t, te } = useI18n();

const channel = route.params.channel as string;
const countrySlug = route.params.country as string;

const bannedSlugs = ['russia', 'russian-federation', 'belarus', 'iran', 'north-korea', 'dprk', 'syria', 'syrian-arab-republic', 'cuba', 'myanmar', 'venezuela', 'afghanistan'];
if (countrySlug && bannedSlugs.includes(countrySlug.toLowerCase())) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true });
}

// Format country slug into Display Name (e.g. united-kingdom -> United Kingdom)
const formatCountryName = (slug: string) => {
  if (!slug) return '';
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const countryName = computed(() => formatCountryName(countrySlug));

// Get the correct translation key for the channel title
const getChannelKey = (ch: string) => {
  if (ch === 'viber-business-calls') return 'mainNav.rates.businessCall';
  if (ch === 'viber-chatbots') return 'mainNav.rates.bots';
  if (ch === 'viber-business-messages') return 'mainNav.rates.businessMess';
  return 'mainNav.rates.businessMess';
};

const getChannelSubtitleKey = (ch: string) => {
  if (ch === 'viber-business-calls') return 'bc.ratesTitle';
  if (ch === 'viber-chatbots') return 'chatbots.ratesTitle';
  if (ch === 'viber-business-messages') return 'bm.ratesTitle';
  return 'bm.ratesTitle';
};

const pageTitle = computed(() => `${t(getChannelKey(channel))} ${t('global.price')} - ${countryName.value}`);
const pageDescription = computed(() => `${t(getChannelKey(channel))} ${t('global.price')} ${countryName.value}. ${t('seo.description')}`);

// Dynamic Capabilities
const whyFeatures = computed(() => {
  return [
    t(`dynamicPricingPages.features.${channel}.f1`),
    t(`dynamicPricingPages.features.${channel}.f2`),
    t(`dynamicPricingPages.features.${channel}.f3`),
    t(`dynamicPricingPages.features.${channel}.f4`),
    t(`dynamicPricingPages.features.${channel}.f5`),
    t(`dynamicPricingPages.features.${channel}.f6`)
  ].filter(f => f && !f.includes('dynamicPricingPages.features'));
});

// Dynamic FAQs (AI/SGE Optimized with Country Profile Fallback)

const getFaqItem = (type: 'q' | 'a', num: number) => {
  const specificKey = `dynamicPricingPages.faq.${channel}.${countrySlug}.${type}${num}`;
  const genericKey = `dynamicPricingPages.faq.${channel}.${type}${num}`;
  
  if (te(specificKey)) {
    return t(specificKey, { country: countryName.value });
  }
  return t(genericKey, { country: countryName.value });
};

const faqItems = computed(() => {
  return [
    {
      question: getFaqItem('q', 1),
      answer: getFaqItem('a', 1),
    },
    {
      question: getFaqItem('q', 2),
      answer: getFaqItem('a', 2),
    },
    {
      question: getFaqItem('q', 3),
      answer: getFaqItem('a', 3),
    },
    {
      question: getFaqItem('q', 4),
      answer: getFaqItem('a', 4),
    }
  ];
});

// Dynamic SEO Injection
useHead({
  titleTemplate: () => pageTitle.value,
  meta: [
    { name: 'description', content: () => pageDescription.value },
    { property: 'og:title', content: () => pageTitle.value },
    { property: 'og:description', content: () => pageDescription.value },
    { property: 'og:type', content: 'article' },
    { name: 'twitter:card', content: 'summary_large_image' },
  ]
});

onMounted(() => {
  const el = document.querySelector("body");
  el?.scrollIntoView();
});
</script>

<template>
  <div class="pricing-dynamic-page pb-20">
    <!-- Hero Section mirroring standard SMSBAT pages -->
    <div class="main-banner bg-gradient" style="background: linear-gradient(135deg, rgba(48, 112, 182, 0.05) 0%, rgba(48, 112, 182, 0.15) 100%); padding: 120px 0 80px 0;">
      <div class="container mx-auto px-4 max-w-7xl text-center">
        <h1 class="text-4xl md:text-5xl font-extrabold mb-6 text-gray-900 leading-tight">
          {{ pageTitle }}
        </h1>
        <p class="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
          {{ $t(getChannelSubtitleKey(channel)) }} {{ countryName }}
        </p>
        <div class="banner-action flex flex-col sm:flex-row items-center justify-center gap-4">
          <template v-if="channel === 'viber-chatbots'">
            <a href="#register-form" class="nav-link button filled inline-block px-10 py-4 rounded-xl bg-[#3070b6] text-white font-bold transition hover:bg-[#255a92] shadow-lg w-full sm:w-auto text-center">
              <p>{{ $t('botReg.regBot') }}</p>
            </a>
            <a href="#pricing" class="nav-link button outlined inline-block px-10 py-4 rounded-xl border-2 border-[#3070b6] text-[#3070b6] font-bold transition hover:bg-[#3070b6] hover:text-white shadow-lg w-full sm:w-auto text-center">
              <p>{{ $t('registerBotLanding.pricing.title') }}</p>
            </a>
          </template>
          <template v-else-if="channel === 'viber-business-calls'">
             <a href="#registration-form" class="nav-link button filled inline-block px-10 py-4 rounded-xl bg-[#3070b6] text-white font-bold transition hover:bg-[#255a92] shadow-lg w-full sm:w-auto text-center">
              <p>{{ $t('bcReg.registerSIP') }}</p>
            </a>
            <NuxtLink :to="localePath('/contacts')" class="nav-link button outlined inline-block px-10 py-4 rounded-xl border-2 border-[#3070b6] text-[#3070b6] font-bold transition hover:bg-[#3070b6] hover:text-white shadow-lg w-full sm:w-auto text-center">
              <p>{{ $t('actions.contactUs') }}</p>
            </NuxtLink>
          </template>
          <template v-else>
            <a href="#registration-form" class="nav-link button filled inline-block px-10 py-4 rounded-xl bg-[#3070b6] text-white font-bold transition hover:bg-[#255a92] shadow-lg w-full sm:w-auto text-center">
              <p>{{ $t('links.registerBusinessAccount') }}</p>
            </a>
            <NuxtLink :to="localePath('/contacts')" class="nav-link button outlined inline-block px-10 py-4 rounded-xl border-2 border-[#3070b6] text-[#3070b6] font-bold transition hover:bg-[#3070b6] hover:text-white shadow-lg w-full sm:w-auto text-center">
              <p>{{ $t('actions.contactUs') }}</p>
            </NuxtLink>
          </template>
        </div>
      </div>
    </div>

    <!-- Dynamic Rates Table Injection -->
    <div class="container mx-auto px-4 max-w-7xl mt-16 mb-20">
      <ClientOnly>
        <div v-if="channel === 'viber-business-calls'">
          <LazyBCDefaultRatesTable :filterCountry="countryName" />
        </div>
        <div v-else-if="channel === 'viber-chatbots'">
          <LazyBotsDefaultRatesTable :filterCountry="countryName" />
        </div>
        <div v-else>
          <LazyBMDefaultRatesTable :filterCountry="countryName" />
        </div>
        <template #fallback>
          <div class="flex justify-center items-center h-64 w-full border border-gray-100 rounded-2xl bg-gray-50/30">
            <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        </template>
      </ClientOnly>
    </div>

    <!-- Commercial Model & Registration Form -->
    <div id="registration-form" class="page-section">
      <div class="container mx-auto px-4 max-w-7xl mb-20">
        <!-- Pricing and Commercial Models -->
        <div id="pricing" class="mb-16 scroll-mt-24">
          <div class="section-head text-center mb-8">
            <div class="text-3xl font-bold text-gray-900">
              <template v-if="channel === 'viber-chatbots'">{{ $t('registerBotLanding.pricing.title') }}</template>
              <template v-else-if="channel === 'viber-business-calls'">{{ $t('bc.pricing.title') }}</template>
              <template v-else>{{ $t('bm.pricing.title') }}</template>
            </div>
          </div>
          
          <div class="pricing-cards mb-8">
            <!-- Registration Fee -->
            <div class="pricing-card">
              <div class="pricing-card-header">
                <h3>
                  <template v-if="channel === 'viber-chatbots'">{{ $t('registerBotLanding.pricing.registration.title') }}</template>
                  <template v-else-if="channel === 'viber-business-calls'">{{ $t('bc.pricing.registration.title') }}</template>
                  <template v-else>{{ $t('bm.pricing.registration.title') }}</template>
                </h3>
                <div class="pricing-amount">
                  <span class="price">
                    <template v-if="channel === 'viber-chatbots' || channel === 'viber-business-calls'">90 EUR</template>
                    <template v-else>0 EUR</template>
                  </span>
                  <span class="price-note">
                    <template v-if="channel === 'viber-chatbots'">{{ $t('registerBotLanding.pricing.vatNote') }}</template>
                    <template v-else-if="channel === 'viber-business-calls'">{{ $t('bc.pricing.vatNote') }}</template>
                    <template v-else>{{ $t('bm.pricing.vatNote') }}</template>
                  </span>
                </div>
              </div>
              <div class="pricing-card-body">
                <p>
                  <template v-if="channel === 'viber-chatbots'">{{ $t('registerBotLanding.pricing.registration.description') }}</template>
                  <template v-else-if="channel === 'viber-business-calls'">{{ $t('bc.pricing.registration.description') }}</template>
                  <template v-else>{{ $t('bm.pricing.registration.description') }}</template>
                </p>
              </div>
            </div>

            <!-- Prepayment -->
            <div class="pricing-card">
              <div class="pricing-card-header">
                <h3>
                  <template v-if="channel === 'viber-chatbots'">{{ $t('registerBotLanding.pricing.prepayment.title') }}</template>
                  <template v-else-if="channel === 'viber-business-calls'">{{ $t('bc.pricing.prepayment.title') }}</template>
                  <template v-else>{{ $t('bm.pricing.prepayment.title') }}</template>
                </h3>
                <div class="pricing-amount">
                  <span class="price">
                    <template v-if="channel === 'viber-chatbots' || channel === 'viber-business-calls'">300 EUR</template>
                    <template v-else>175 EUR</template>
                  </span>
                  <span class="price-note">
                    <template v-if="channel === 'viber-chatbots'">{{ $t('registerBotLanding.pricing.vatNote') }}</template>
                    <template v-else-if="channel === 'viber-business-calls'">{{ $t('bc.pricing.vatNote') }}</template>
                    <template v-else>{{ $t('bm.pricing.vatNote') }}</template>
                  </span>
                </div>
              </div>
              <div class="pricing-card-body">
                <p>
                  <template v-if="channel === 'viber-chatbots'">{{ $t('registerBotLanding.pricing.prepayment.description') }}</template>
                  <template v-else-if="channel === 'viber-business-calls'">{{ $t('bc.pricing.prepayment.description') }}</template>
                  <template v-else>{{ $t('bm.pricing.prepayment.description') }}</template>
                </p>
              </div>
            </div>
          </div>

          <!-- Total to Start -->
          <div class="total-start mb-16">
            <div class="total-start-content">
              <h3>
                <template v-if="channel === 'viber-chatbots'">{{ $t('registerBotLanding.pricing.total.title') }}</template>
                <template v-else-if="channel === 'viber-business-calls'">{{ $t('bc.pricing.total.title') }}</template>
                <template v-else>{{ $t('bm.pricing.total.title') }}</template>
              </h3>
              <div class="total-amount">
                <span class="total-price">
                  <template v-if="channel === 'viber-chatbots' || channel === 'viber-business-calls'">390 EUR</template>
                  <template v-else>175 EUR</template>
                </span>
                <span class="total-note">
                  <template v-if="channel === 'viber-chatbots'">{{ $t('registerBotLanding.pricing.vatNote') }}</template>
                  <template v-else-if="channel === 'viber-business-calls'">{{ $t('bc.pricing.vatNote') }}</template>
                  <template v-else>{{ $t('bm.pricing.vatNote') }}</template>
                </span>
              </div>
              <p class="total-breakdown">
                <template v-if="channel === 'viber-chatbots'">{{ $t('registerBotLanding.pricing.total.breakdown') }}</template>
                <template v-else-if="channel === 'viber-business-calls'">{{ $t('bc.pricing.total.breakdown') }}</template>
                <template v-else>{{ $t('bm.pricing.total.breakdown') }}</template>
              </p>
            </div>
          </div>

          <template v-if="channel === 'viber-chatbots'">
            <div class="section-head text-center mb-8">
              <div class="text-3xl font-bold text-gray-900">{{ $t('registerBotLanding.commercial.title') }}</div>
              <p class="mt-3 text-gray-600 text-lg">{{ $t('registerBotLanding.commercial.subtitle') }}</p>
            </div>
            <div class="commercial-card max-w-4xl mx-auto bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition hover:-translate-y-1">
              <div class="commercial-card-content flex flex-col md:flex-row items-center gap-8 p-8 md:p-10">
                <div class="commercial-icon shrink-0 w-24 h-24 rounded-2xl flex items-center justify-center text-white" style="background: linear-gradient(135deg, #3070b6, #044068)">
                  <span class="pi pi-calendar text-5xl"></span>
                </div>
                <div class="commercial-details flex-1 text-center md:text-left">
                  <h3 class="text-2xl font-bold text-gray-900 mb-3">{{ $t('registerBotLanding.pricing.monthly.title') }}</h3>
                  <p class="text-gray-600 text-lg leading-relaxed">{{ $t('registerBotLanding.pricing.monthly.description') }}</p>
                </div>
                <div class="commercial-price flex flex-col items-center text-center shrink-0">
                  <span class="text-4xl font-extrabold text-[#3070b6]">100 EUR</span>
                  <span class="text-gray-500 font-medium mt-1">/ {{ $t('global.month') }}</span>
                  <span class="text-sm text-gray-400 mt-2">{{ $t('registerBotLanding.pricing.vatNote') }}</span>
                </div>
              </div>
              <div class="commercial-note bg-amber-50 text-amber-900 p-5 flex items-center justify-center text-base font-semibold">
                <span class="pi pi-info-circle mr-3 text-xl"></span>
                {{ $t('registerBotLanding.pricing.monthly.startDate') }}
              </div>
            </div>
          </template>
        </div>

        <!-- How To Start Funnel -->
        <HowToStart :channel="channel" :country="countryName" />

        <!-- Why SMSBAT / Features Block -->
        <div class="why-us-section mt-20 mb-20 scroll-mt-24">
          <div class="text-center mb-10">
            <h2 class="text-3xl font-bold text-gray-900">
              {{ $t('dynamicPricingPages.whyTitle', { channel: $t(getChannelKey(channel)) }) }}
            </h2>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div 
              v-for="(feature, index) in whyFeatures" 
              :key="index"
              class="feature-card flex items-start gap-4 p-6 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
            >
              <div class="feature-icon shrink-0 w-12 h-12 rounded-xl bg-blue-50 text-[#3070b6] flex items-center justify-center">
                <i class="pi pi-check-circle text-xl"></i>
              </div>
              <div class="feature-content pt-1">
                <h3 class="font-semibold text-gray-900 text-lg leading-tight">{{ feature }}</h3>
              </div>
            </div>
          </div>
        </div>

        <!-- Dynamic Form Rendering -->
        <div id="register-form" class="bg-white p-8 md:p-12 rounded-2xl shadow-xl border border-gray-100 max-w-4xl mx-auto scroll-mt-24">
          <div class="section-head text-center mb-10">
            <div class="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
              <template v-if="channel === 'viber-chatbots'">
                {{ $t('registerBotLanding.form.title') }}
              </template>
              <template v-else-if="channel === 'viber-business-calls'">
                {{ $t('bcReg.registerSIP') }}
              </template>
              <template v-else>
                {{ $t('addAccount.infoTitle') }}
              </template>
            </div>
            <p v-if="channel === 'viber-chatbots'" class="mt-4 text-gray-600 text-lg">
              {{ $t('registerBotLanding.form.subtitle') }}
            </p>
          </div>
          
          <ClientOnly>
            <div v-if="channel === 'viber-business-calls'">
              <LazyRegisterBC />
            </div>
            <div v-else-if="channel === 'viber-chatbots'">
              <LazyRegisterBotForm />
            </div>
            <div v-else>
              <LazyRegisterCA />
            </div>
            <template #fallback>
              <div class="flex justify-center items-center h-64">
                <i class="pi pi-spin pi-spinner text-4xl text-blue-500"></i>
              </div>
            </template>
          </ClientOnly>
        </div>
      </div>
    </div>
    
    <div class="page-section bg-gray-50 border-t border-gray-100">
      <div class="container max-w-4xl mx-auto px-4">
         <PopularQuestions :title="$t('dynamicPricingPages.faqTitle')" :questions="faqItems" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  height: auto;
  line-height: 1.3;
  font-size: 18px;
  text-decoration: none;
  cursor: pointer;
}

/* Extracted Pricing Cards CSS */
.pricing-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
}

.pricing-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  position: relative;
  border: 2px solid transparent;
  transition: transform 0.2s, box-shadow 0.2s;
}

.pricing-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
}

.pricing-card-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.pricing-card-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
}

.pricing-amount {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.price {
  font-size: 2.5rem;
  font-weight: 700;
  color: #7c3aed;
}

.price-note {
  font-size: 0.75rem;
  color: #9ca3af;
}

.pricing-card-body {
  text-align: center;
  color: #6b7280;
  font-size: 0.95rem;
  line-height: 1.6;
}

/* Traffic Info */
.traffic-info {
  margin-top: 2rem;
  background: #eff6ff;
  border-radius: 12px;
  padding: 1.5rem;
}

.traffic-link {
  color: #7c3aed;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  transition: color 0.2s;
}

.traffic-link:hover {
  color: #5b21b6;
}

/* Total to Start */
.total-start {
  margin-top: 2rem;
  background: linear-gradient(135deg, #3070b6, #044068);
  border-radius: 16px;
  padding: 2rem;
  color: white;
}

.total-start-content {
  text-align: center;
}

.total-start h3 {
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 1rem;
  opacity: 0.9;
}

.total-amount {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 1rem;
}

.total-price {
  font-size: 3rem;
  font-weight: 700;
}

.total-note {
  font-size: 0.85rem;
  opacity: 0.8;
}

.total-breakdown {
  font-size: 0.95rem;
  opacity: 0.9;
}
</style>
